import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, ArrowRight, Loader2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail, useAuth } from "../lib/auth";

// BTC3 Phase 8 F2 — one applicant-facing presentation map keyed on
// `display_status` (not raw enums).  Backend returns both the
// authoritative `status` and the applicant-facing `display_status`;
// this view renders ONLY the latter.
const DISPLAY_META = {
  received:               { label: "Application received", tone: "blue",    icon: Clock,         blurb: "BTC has received your application. A coordinator will begin review shortly." },
  in_review:              { label: "Under review",         tone: "amber",   icon: Clock,         blurb: "A BTC coordinator is reviewing your application." },
  needs_information:      { label: "BTC needs information", tone: "orange", icon: AlertCircle,   blurb: "BTC has asked for more information — please respond below." },
  approved_pending_setup: { label: "Approved — setup in progress", tone: "indigo", icon: Clock,  blurb: "Approved. BTC is working with you on the next steps before you go live." },
  active:                 { label: "Active",                tone: "emerald", icon: CheckCircle2, blurb: "You are an active BTC participant." },
  declined:               { label: "Not approved",          tone: "rose",    icon: AlertCircle,  blurb: "This application was not approved on this occasion." },
};

const TYPE_LABEL = {
  volunteer:    "Volunteer application",
  organization: "Organisation application",
  buddy:        "Buddy application",
};

const TONE = {
  blue:    "bg-blue-50 text-blue-800 border-blue-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  orange:  "bg-orange-50 text-orange-800 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rose:    "bg-rose-50 text-rose-800 border-rose-200",
  indigo:  "bg-indigo-50 text-indigo-800 border-indigo-200",
};

export default function MyApplications() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setFetching(true);
    api.get("/ops/my-applications")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load applications."))
      .finally(() => setFetching(false));
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/my-applications" replace />;

  return (
    <div className="bg-white min-h-screen" data-testid="my-applications-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-[#1A5FAB]/10 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-[#1A5FAB]" strokeWidth={1.75} />
          </div>
          <p className="btc-eyebrow">MY APPLICATIONS</p>
        </div>
        <h1 className="text-3xl font-extrabold text-[#0B132B] mb-2">Your applications with BTC</h1>
        <p className="text-[#4B5563] mb-8 text-sm">
          Anything you've submitted to BTC — as a volunteer, a Buddy applicant, or an organisation — appears here so
          you can see where things stand and respond if BTC needs more information.
        </p>

        {/* BTC3 Phase 11 Stage A1 · Truthful UI — Anonymous application
            claim.  The underlying claim-token flow requires verified
            email delivery, and email-provider integration has not been
            authorized in this stage.  Rather than show
            "check your email" copy that cannot be honoured, surface a
            truthful disabled state.  No request action is exposed. */}
        <div
          className="rounded-xl border border-slate-200 bg-slate-50 p-5 mb-8"
          data-testid="anonymous-claim-unavailable"
          role="status"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" aria-hidden="true" />
            <div className="text-sm text-slate-700">
              <div className="font-semibold text-slate-900 mb-1">
                Claiming an earlier anonymous application is not available right now.
              </div>
              <p>
                If you submitted an application without being signed in, BTC
                cannot currently link it to your account from the portal.
                Please contact the BTC team directly and they will help
                reconcile it manually.
              </p>
            </div>
          </div>
        </div>

        {err && <div className="text-sm text-red-700 mb-4">{err}</div>}
        {fetching && <div className="text-sm text-slate-500">Loading applications…</div>}

        {!fetching && items.length === 0 && (
          <div className="rounded-xl border border-slate-200 p-8 text-center">
            <p className="text-slate-600 mb-4">You have not submitted any applications yet.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/volunteer/apply" className="btc-btn-primary" data-testid="my-apps-volunteer-cta">Volunteer with BTC <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/organization/apply" className="btc-btn-secondary" data-testid="my-apps-organization-cta">Partner with BTC</Link>
            </div>
          </div>
        )}

        <div className="space-y-5" data-testid="my-applications-list">
          {items.map((a) => <ApplicationCard key={a.id} a={a} onChange={load} />)}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ApplicationCard({ a, onChange }) {
  const bucket = a.display_status || "received";
  const meta = DISPLAY_META[bucket] || DISPLAY_META.received;
  const Icon = meta.icon;
  const pendingReq = (a.info_requests || []).filter((r) => !r.response_text).slice(-1)[0];
  const typeLabel = TYPE_LABEL[a.type] || a.type;
  const title = a.type === "organization" ? (a.organization_name || "Your organisation") : (a.applicant_name || "Your application");
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="my-application-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs text-slate-500 uppercase mb-1" data-testid="my-application-type">{typeLabel}</div>
          <div className="font-semibold text-slate-900" data-testid="my-application-title">
            {title}
          </div>
          <div className="text-xs text-slate-500 mt-1">Submitted {(a.created_at || "").slice(0, 10)}</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${TONE[meta.tone]}`} data-testid={`my-application-display-${bucket}`}>
          <Icon className="w-3.5 h-3.5" /> {meta.label}
        </span>
      </div>
      <p className="text-sm text-slate-600 mt-3">{meta.blurb}</p>

      {pendingReq && (
        <RespondBox appId={a.id} req={pendingReq} onChange={onChange} />
      )}

      {(a.info_requests || []).length > 0 && !pendingReq && (
        <div className="mt-4 border-t pt-3">
          <div className="text-xs text-slate-500 mb-2">Previous information exchanges</div>
          <ul className="space-y-2">
            {(a.info_requests || []).slice(-3).map((r) => (
              <li key={r.id} className="text-xs text-slate-600" data-testid="my-application-info-history">
                <div><span className="font-semibold">BTC asked:</span> {r.request_text}</div>
                {r.response_text && <div className="mt-1"><span className="font-semibold">You replied:</span> {r.response_text}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RespondBox({ appId, req, onChange }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (txt.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/ops/my-applications/${appId}/respond`, { response: txt.trim() });
      toast.success("Response sent to BTC.");
      setTxt("");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit response.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="my-application-respond">
      <div className="text-xs font-semibold text-orange-900 uppercase mb-1">BTC has asked</div>
      <p className="text-sm text-slate-800 mb-3">{req.request_text}</p>
      <textarea
        value={txt} onChange={(e) => setTxt(e.target.value)} rows={4}
        placeholder="Your response to BTC…"
        className="w-full text-sm border border-orange-200 rounded px-3 py-2 focus:outline-none focus:border-orange-400"
        data-testid="my-application-respond-text"
      />
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={busy || txt.trim().length < 2} className="btc-btn-primary text-sm" data-testid="my-application-respond-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send response <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </form>
  );
}
